<template>
	<SiteScannerLayout>
		<template #header-title>
			<div>
				<span>
					Scan results - {{ scanName }}
				</span>
				<span v-if="gpcDetected">
					<v-tooltip right>
						<template
							#activator="{ on, attrs }"
						>
							<v-chip
								class="ml-2 prussian-blue white--text"
								small
								v-bind="attrs"
								v-on="on"
							>
								GPC File Found
							</v-chip>
						</template>
						<span>
							A GPC file was found on the site scanned.  The existence of this file indicates GPC is being used as part of this sites compliance with privacy laws and that the site complies with GPC within the context it was understood to apply
						</span>
					</v-tooltip>
				</span>
			</div>
		</template>
		<template #header-after>
			<Tabs
				:tab.sync="tab"
				:tabs="tabs"
			/>
		</template>
		<template #content>
			<TabItems
				:tab="tab"
				:tabs="tabs"
			>
				<template #0>
					<SummaryTab
						:scan-guid="scanGuid"
						:user-can-use-site-scanner="userCanUseSiteScanner"
						@set-scan-name="scanName = $event"
						@set-scan-caption="setScanCaption"
						@set-gpc-detected="setGpcDetected"
					/>
				</template>
				<template #1>
					<ScanActivityTab
						:scan-guid="scanGuid"
						:scan-activity-csv-exists="csvsExist.scanActivityCsvExists"
					/>
				</template>
				<template #2>
					<CookiesTab
						:scan-guid="scanGuid"
						:user-can-use-site-scanner="userCanUseSiteScanner"
					/>
				</template>
				<template #3>
					<TrackersTab
						:scan-guid="scanGuid"
						:user-can-use-site-scanner="userCanUseSiteScanner"
					/>
				</template>
				<template #4>
					<ResourcesTab
						:scan-guid="scanGuid"
						:user-can-use-site-scanner="userCanUseSiteScanner"
					/>
				</template>
			</TabItems>
		</template>
		<template #footer>
			<PageActionRow>
				<template #actions>
					<SecondaryActionButton @click="backToOverview">
						Back to Overview
					</SecondaryActionButton>
					<v-spacer />
					<div
						v-if="tab === COOKIES_TAB"
						class="d-flex align-center"
					>
						<PrimaryActionButton
							:disabled="isGeneratingCsv"
							@click="handleCookiesDownload(false)"
						>
							{{ isGeneratingCsv ? 'Regenerating CSV...' : 'Download Cookies CSV' }}
						</PrimaryActionButton>
						<v-tooltip top>
							<template #activator="{ on, attrs }">
								<v-btn
									v-if="!isGeneratingCsv"
									icon
									class="ml-2"
									v-bind="attrs"
									:disabled="forceFreshDisabled"
									v-on="on"
									@click="handleCookiesDownload(true)"
								>
									<v-icon small>
										mdi-refresh
									</v-icon>
								</v-btn>
							</template>
							<span>
								{{ refreshCooldown ? 'Please wait… recently refreshed' : 'Regenerate CSV file (use to force refresh if your cookie changes were not immediately visible when downloading)' }}
							</span>
						</v-tooltip>
					</div>
					<PrimaryActionButton
						v-if="tab === SCAN_ACTIVITY_TAB"
						:disabled="!csvsExist.scanActivityCsvExists"
						@click="downloadCsv(downloadTypeEnum.SCAN_ACTIVITY)"
					>
						Download URL Status Report
					</PrimaryActionButton>
					<PrimaryActionButton
						v-if="tab === TRACKERS_TAB"
						:disabled="!csvsExist.trackersCsvExists"
						@click="downloadCsv(downloadTypeEnum.ADVANCED_TRACKERS)"
					>
						Download Trackers CSV
					</PrimaryActionButton>
					<PrimaryActionButton
						v-if="tab === RESOURCES_TAB"
						:disabled="!csvsExist.resourcesCsvExists"
						@click="downloadCsv(downloadTypeEnum.SUB_RESOURCES)"
					>
						Download Resources CSV
					</PrimaryActionButton>
					<PrimaryActionButton
						class="ml-2"
						@click="downloadReport"
					>
						Download Report
					</PrimaryActionButton>
				</template>
			</PageActionRow>
		</template>
	</SiteScannerLayout>
</template>

<script>
import { mapGetters } from 'vuex'
import SiteScannerLayout from '../site-scanner-layout.vue'
import Tabs from '../../../../../../../shared/components/tabs.vue'
import TabItems from '../../../../../../../shared/components/tab-items.vue'
import SummaryTab from './summary-tab.vue'
import CookiesTab from './cookies-tab.vue'
import TrackersTab from './trackers-tab.vue'
import ResourcesTab from './resources-tab.vue'
import ScanActivityTab from './scan-activity-tab.vue'
import PageActionRow from '../../../../../../../shared/components/page-action-row.vue'
import SecondaryActionButton from '../../../../../../../shared/components/secondary-action-button.vue'
import PrimaryActionButton from '../../../../../../../shared/components/primary-action-button.vue'
import { SITE_SCANNER } from '../../../../../router/route-names.js'
import { downloadTypeEnum } from '../download-type-enums.js'
import { downloadCsvFromS3, downloadScanFile, validateCsvExists, getCsvStatuses } from '../../../../../../../shared/utils/api/site-scanner.js'
import { showSnackbar } from '../../../../../../../shared/state/snackbar.js'
import { SITE_SCANNER_READ_ONLY } from '../../../../../../../shared/permissions/admin-portal-permissions.js'
import downloadCSVMixin from '../../../../../../../shared/mixins/download-csv.js'
import generatePDFMixin from '../../../../../../../shared/mixins/generate-pdf.js'

export default {
	components: {
		SiteScannerLayout,
		Tabs,
		TabItems,
		SummaryTab,
		CookiesTab,
		TrackersTab,
		ResourcesTab,
		PageActionRow,
		SecondaryActionButton,
		PrimaryActionButton,
		ScanActivityTab
	},
	mixins: [downloadCSVMixin, generatePDFMixin],
	props: {
		scanGuid: String,
		maxUrls: Number
	},
	setup () {
		return {
			downloadTypeEnum
		}
	},
	data () {
		const SCAN_ACTIVITY_TAB = '1'
		const COOKIES_TAB = '2'
		const TRACKERS_TAB = '3'
		const RESOURCES_TAB = '4'
		return {
			tab: '0',
			tabs: [
				{ title: 'Summary', slot: '0' },
				{ title: 'Scan Activity', slot: '1' },
				{ title: 'Cookies', slot: '2' },
				{ title: 'Trackers', slot: '3' },
				{ title: 'Resources', slot: '4' }
			],
			COOKIES_TAB,
			TRACKERS_TAB,
			RESOURCES_TAB,
			SCAN_ACTIVITY_TAB,
			scanName: '',
			scanCaption: 'Your Scan Results',
			gpcDetected: false,
			// Tracks whether the most recent download/regeneration attempt failed (HTTP/network/API)
			errorDuringAttempt: false,
			csvsExist: {
				cookiesCsvExists: false,
				trackersCsvExists: false,
				resourcesCsvExists: false,
				scanActivityCsvExists: false
			},
			// Adaptive CSV polling state
			csvStatus: null,
			isGeneratingCsv: false,
			pollAttempts: 0,
			nextDelay: 2000,
			pollTimer: null,
			// Cached fresh CSV blob + filename when a force-fresh inline regeneration returns bytes
			cachedCookieCsv: null,
			cachedCookieCsvFileName: null,
			refreshCooldown: false,
			refreshCooldownTimer: null
		}
	},
	computed: {
		...mapGetters('auth', ['productAreaPermission']),
		userCanUseSiteScanner () {
			return this.productAreaPermission(SITE_SCANNER_READ_ONLY)
		},
		forceFreshDisabled () {
			return this.isGeneratingCsv || this.refreshCooldown
		},
		hasCachedCookieCsv () { return !!this.cachedCookieCsv }
	},
	created () {
		this.getCsvExists(this.scanGuid)
		this.refreshCookieStatus()
	},
	beforeDestroy () {
		this.clearPoll()
	},
	methods: {
		// ----- CSV Polling Logic (Cookies) -----
		async handleCookiesDownload (forceFresh) {
			// If not forcing fresh and we already have a cached blob (inline regen), just download it
			if (!forceFresh && this.cachedCookieCsv) {
				this.generateCsv(this.cachedCookieCsv, this.cachedCookieCsvFileName || 'Cookies.csv')
				this.clearCachedCsv()
				return
			}
			try {
				// Reset error flag for a fresh attempt
				this.errorDuringAttempt = false
				const response = await downloadScanFile(this.scanGuid, downloadTypeEnum.COOKIE_REPORT, null, forceFresh)
				const dispo = response?.headers?.['content-disposition']
				if (dispo) {
					const fileName = dispo.split('filename=')[1].split(';')[0]
					if (forceFresh) {
						// Cache instead of immediate download for inline force fresh
						this.cachedCookieCsv = response.data
						this.cachedCookieCsvFileName = fileName
						this.beginRefreshCooldown()
						// Mark status as available (regenerated). Refresh statuses for timestamp / badges
						await this.refreshCookieStatus()
						this.isGeneratingCsv = false
						showSnackbar('Cookie CSV regeneration complete. Ready for download.')
					} else {
						this.generateCsv(response.data, fileName)
						this.clearCachedCsv()
					}
					return
				}
				// If API returns an existing S3 URL, download from there
				const body = response?.data
				if (body && body.successful && body.responseFileName && !body.responseBytes) {
					this.isGeneratingCsv = false
					this.clearCachedCsv()
					downloadCsvFromS3(body.responseFileName)
					return
				}
				// Queued path: start polling and clear any stale cache
				this.cachedCookieCsv = null
				this.cachedCookieCsvFileName = null
				this.startPolling()
			} catch (e) {
				// Flag that we are entering polling due to an error so we do not show a misleading success snackbar
				this.errorDuringAttempt = true
				this.cachedCookieCsv = null
				this.cachedCookieCsvFileName = null
				this.startPolling()
			}
		},
		clearCachedCsv () {
			this.cachedCookieCsv = null
			this.cachedCookieCsvFileName = null
		},
		beginRefreshCooldown () {
			this.refreshCooldown = true
			if (this.refreshCooldownTimer) {
				clearTimeout(this.refreshCooldownTimer)
			}
			this.refreshCooldownTimer = setTimeout(() => {
				this.refreshCooldown = false
				this.refreshCooldownTimer = null
			}, 7000)
		},
		async refreshCookieStatus () {
			// Reuse existing validate + status endpoint for availability nuance
			await this.getCsvExists(this.scanGuid)
			const statuses = await this.fetchCsvStatuses()
			const cookieType = downloadTypeEnum.COOKIE_REPORT
			this.csvStatus = statuses.find(s => s.contentTypeId === cookieType) || null
		},
		async fetchCsvStatuses () {
			return await getCsvStatuses(this.scanGuid)
		},
		startPolling () {
			this.isGeneratingCsv = true
			this.pollAttempts = 0
			this.nextDelay = 2000
			this.fetchAndSchedule()
		},
		clearPoll () {
			if (this.pollTimer) {
				clearTimeout(this.pollTimer)
				this.pollTimer = null
			}
		},
		async fetchAndSchedule () {
			await this.refreshCookieStatus()
			if (this.csvStatus?.isAvailable) {
				this.isGeneratingCsv = false
				this.clearPoll()
				// Only show success if we weren't polling as a fallback after an error.
				if (!this.errorDuringAttempt) {
					showSnackbar('Cookie CSV regeneration complete. Ready for download.')
				} else {
					// Clear the error flag so subsequent genuine regenerations can surface success.
					this.errorDuringAttempt = false
				}
				return
			}
			this.pollAttempts++
			if (this.pollAttempts > 15) {
				this.isGeneratingCsv = false
				this.clearPoll()
				return
			}
			this.nextDelay = Math.min(Math.round(this.nextDelay * 1.5), 15000)
			this.clearPoll()
			this.pollTimer = setTimeout(() => this.fetchAndSchedule(), this.nextDelay)
		},
		backToOverview () {
			this.$router.push({ name: SITE_SCANNER })
		},
		setScanCaption (captionDetails) {
			this.scanCaption = `Your scan results for Scan ID ${captionDetails.currentScanId}. The successful scan prior to this one is Scan ID ${captionDetails.previousScanId}.`
		},
		async downloadReport () {
			const response = await downloadScanFile(this.scanGuid, downloadTypeEnum.PDF, null)
			const fileName = response.headers['content-disposition'].split('filename=')[1].split(';')[0]
			this.generatePdf(response.data, fileName)
		},
		async getCsvExists (scanGuid) {
			this.csvsExist = await validateCsvExists(scanGuid)
		},
		async downloadCsv (downloadType) {
			const response = await downloadScanFile(this.scanGuid, downloadType, null)
			downloadCsvFromS3(response.data.responseFileName)
		},
		setGpcDetected (gpcDetected) {
			this.gpcDetected = gpcDetected
		}
	}
}
</script>
